import {isNotNil} from 'ramda';

import type {BusinessCaseResponseBody} from '@dms/api/sales';
import {getCustomerName} from '@dms/shared';

import {
  ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS,
  DEFAULT_BUSINESS_CASE_SIGNAL_POLICY,
  detectBusinessCaseSignals,
  type AssistantBusinessCaseContext,
} from '@prompt-bridge/shared/ai-assistant-core';

import {EMPTY_PLACEHOLDER} from 'shared/web';

import type {ToBusinessCaseAssistantContextInput} from '../types/ToBusinessCaseAssistantContextInput';
import type {SupportedBusinessCaseForAssistant} from '../types/ToBusinessCaseFactsInput';
import {resolveAssistantBusinessCaseLanguage} from './resolveAssistantBusinessCaseLanguage';
import {toBoundedAssistantBusinessCaseText} from './toBoundedAssistantBusinessCaseText';
import {toBusinessCaseFacts} from './toBusinessCaseFacts';
import {toBusinessCaseState} from './toBusinessCaseState';
import {toBusinessCaseType} from './toBusinessCaseType';
import {toReadableBusinessCaseCode} from './toReadableBusinessCaseCode';

export const isSupportedBusinessCaseForAssistant = (
  businessCase: BusinessCaseResponseBody
): businessCase is SupportedBusinessCaseForAssistant =>
  isNotNil(toBusinessCaseType(businessCase.businessCaseType)) &&
  isNotNil(toBusinessCaseState(businessCase.businessCaseState));

export function toBusinessCaseAssistantContext(
  input: ToBusinessCaseAssistantContextInput
): AssistantBusinessCaseContext | null {
  if (!isSupportedBusinessCaseForAssistant(input.businessCase)) {
    return null;
  }

  const facts = toBusinessCaseFacts({...input, businessCase: input.businessCase});
  const policy = input.policy ?? DEFAULT_BUSINESS_CASE_SIGNAL_POLICY;
  const language = resolveAssistantBusinessCaseLanguage(
    input.customer.preferredLocale,
    input.tenant.defaultLocale,
    input.tenant.availableLocales
  );
  const customerName = getCustomerName(input.customer);
  const displayName =
    customerName === EMPTY_PLACEHOLDER
      ? undefined
      : toBoundedAssistantBusinessCaseText(customerName);
  const publicId = toBoundedAssistantBusinessCaseText(
    toReadableBusinessCaseCode(input.businessCase.customCode ?? input.businessCase.code)
  );
  const sourceLabel = toBoundedAssistantBusinessCaseText(input.sourceLabel);
  const interestId = toBoundedAssistantBusinessCaseText(input.businessCase.interestId);
  const origin =
    isNotNil(sourceLabel) || isNotNil(interestId)
      ? {
          ...(isNotNil(interestId) ? {interestId} : {}),
          ...(isNotNil(sourceLabel) ? {sourceLabel} : {}),
        }
      : undefined;
  const plannedActivities = facts.plannedActivities.map((activity) => {
    const task = input.upcomingTasks.find((candidate) => candidate.id === activity.id);
    const title = toBoundedAssistantBusinessCaseText(task?.title);
    return {
      ...activity,
      id: activity.id.slice(0, ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS),
      ...(isNotNil(title) ? {title} : {}),
    };
  });

  return {
    businessCaseId: input.businessCase.id.slice(0, ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS),
    publicId: publicId ?? null,
    branchId: input.businessCase.branchId.slice(0, ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS),
    customerId: input.businessCase.customerId.slice(0, ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS),
    contactPersonId: input.businessCase.contactPersonId,
    businessCaseType: facts.type,
    businessCaseState: facts.state,
    createdAt: facts.createdAt,
    updatedAt: facts.updatedAt,
    customerLanguage: language.preferredLocale,
    userLanguage: input.userLanguage.slice(0, ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS),
    customer: {
      id: input.customer.id.slice(0, ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS),
      ...(isNotNil(displayName) ? {displayName} : {}),
      ...language,
    },
    ...(isNotNil(input.sender) ? {sender: input.sender} : {}),
    ...(isNotNil(origin) ? {origin} : {}),
    facts: {
      kind: facts.kind,
      ...(isNotNil(facts.saleVehicle) ? {saleVehicle: facts.saleVehicle} : {}),
      ...(isNotNil(facts.purchaseVehicle) ? {purchaseVehicle: facts.purchaseVehicle} : {}),
      ...(isNotNil(facts.onlineOffer) ? {onlineOffer: facts.onlineOffer} : {}),
      availableActions: [...(facts.availableActions ?? [])],
      ...(isNotNil(facts.margin) ? {margin: facts.margin} : {}),
      incompleteChecklistItemCount: facts.incompleteChecklistItemCount,
      payments: [...facts.payments],
      completeness: {
        timelineComplete: facts.completeness.timelineComplete,
        omittedTimelineEventCount: facts.completeness.omittedTimelineEventCount,
        plannedActivitiesComplete: facts.completeness.plannedActivitiesComplete,
        omittedPlannedActivityCount: facts.completeness.omittedPlannedActivityCount,
        checklistComplete: facts.completeness.checklistComplete,
        paymentEvidenceComplete: facts.completeness.paymentEvidenceComplete,
        offerEvidenceComplete: facts.completeness.offerEvidenceComplete,
        vehicleLoaded: facts.completeness.vehicleLoaded ?? false,
        actionsLoaded: facts.completeness.actionsLoaded ?? false,
        checkoutLoaded: facts.completeness.checkoutLoaded ?? false,
        checklistLoaded: facts.completeness.checklistLoaded ?? false,
        testDrivesLoaded: facts.completeness.testDrivesLoaded ?? false,
        messagesLoaded: facts.completeness.messagesLoaded ?? false,
      },
    },
    timeline: [...facts.timeline],
    plannedActivities,
    signals: detectBusinessCaseSignals({facts, policy, evaluatedAt: input.evaluatedAt}),
    policy: {
      coldThresholdDays: policy.coldThresholdDays,
      awaitingReplyMinimumHours: policy.awaitingReplyMinimumHours,
      offerNotSentMinimumDays: policy.offerNotSentMinimumDays,
      offerSentNoResponseMinimumDays: policy.offerSentNoResponseMinimumDays,
      offerExpiringDays: policy.offerExpiringDays,
      testDriveFollowUpMinimumDays: policy.testDriveFollowUpMinimumDays,
      testDriveSoonHours: policy.testDriveSoonHours,
      paymentUnpaidMinimumDays: policy.paymentUnpaidMinimumDays,
    },
    evaluatedAt: input.evaluatedAt,
  };
}
